import { useEffect, useState, useCallback, useRef } from "react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem
} from "./ui/command";
import { api, type FileRecord } from "~/services/api";
import {
  FileIcon,
  FileTextIcon,
  FileImageIcon,
  FileVideoIcon,
  FileAudioIcon,
  FolderIcon,
  LoaderIcon,
  ClockIcon
} from "lucide-react";
import { cn } from "~/lib/utils";

const DEBOUNCE_MS = 300;

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Global keyboard shortcut listener
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Listen for custom event to open search (from navbar input)
  useEffect(() => {
    const handleOpenSearch = () => setOpen(true);
    window.addEventListener("open-global-search", handleOpenSearch);
    return () => window.removeEventListener("open-global-search", handleOpenSearch);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setFiles([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const results = await api.searchFiles(query, 20);
      setFiles(results);
    } catch (error) {
      console.error("Search failed:", error);
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    if (search) {
      setIsLoading(true);
      debounceTimerRef.current = setTimeout(() => {
        performSearch(search);
      }, DEBOUNCE_MS);
    } else {
      setFiles([]);
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search, performSearch]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setSearch("");
      setFiles([]);
      setIsLoading(false);
    }
  }, [open]);

  const getFileIcon = (file: FileRecord) => {
    const mimeType = file.mime_type || "";
    const ext = file.filename.toLowerCase().split(".").pop() || "";

    if (mimeType.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
      return <FileImageIcon className="size-4 text-blue-500" />;
    }
    if (mimeType.startsWith("video/") || ["mp4", "webm", "mov", "avi"].includes(ext)) {
      return <FileVideoIcon className="size-4 text-purple-500" />;
    }
    if (mimeType.startsWith("audio/") || ["mp3", "wav", "ogg", "aac"].includes(ext)) {
      return <FileAudioIcon className="size-4 text-green-500" />;
    }
    if (mimeType === "application/pdf" || ext === "pdf") {
      return <FileTextIcon className="size-4 text-red-500" />;
    }
    if (mimeType.startsWith("text/") || ["txt", "md", "json", "xml"].includes(ext)) {
      return <FileTextIcon className="size-4 text-orange-500" />;
    }

    return <FileIcon className="size-4 text-gray-500" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };


  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search Files"
      description="Search for files by name"
      showCloseButton={false}
      className="max-w-2xl"
    >
      <CommandInput
        placeholder="Search files by name..."
        value={search}
        onValueChange={setSearch}
        className="h-12 text-base"
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8">
              <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : search ? (
            <div className="py-8">
              <p className="text-sm text-muted-foreground">No files found.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="py-8">
              <p className="text-sm text-muted-foreground">
                Type to search your files
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted rounded">⌘K</kbd> to close
              </p>
            </div>
          )}
        </CommandEmpty>

        {files.length > 0 && (
          <CommandGroup heading={`${files.length} file${files.length === 1 ? '' : 's'} found`}>
            {files.map((file) => (
              <CommandItem
                key={file.id}
                value={file.filename}
                className="flex items-center gap-3 px-3 py-3"
              >
                <div className="flex-shrink-0">
                  {getFileIcon(file)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      {file.filename}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="size-3" />
                      {formatDate(file.created_at)}
                    </span>
                    <span>{formatFileSize(file.size)}</span>
                    {file.mime_type && (
                      <span className="px-1.5 py-0.5 rounded-sm bg-muted">
                        {file.mime_type.split('/')[1] || file.mime_type}
                      </span>
                    )}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      {/* Footer hint */}
      <div className="border-t px-4 py-2 text-xs text-muted-foreground flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 font-semibold bg-background rounded border">↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 font-semibold bg-background rounded border">esc</kbd>
            close
          </span>
        </div>
        {isLoading && (
          <span className="flex items-center gap-1.5">
            <LoaderIcon className="size-3 animate-spin" />
            searching...
          </span>
        )}
      </div>
    </CommandDialog>
  );
}
